import { z } from 'zod';

/**
 * Registro de feature flags por empresa (`tenants.feature_flags`). Sólo se aceptan claves de este
 * registro: una clave mal escrita no puede "prender" nada por error.
 *
 * `superadminOnly`: sólo el superadmin la cambia (p. ej. la escritura hacia la DB del stack viejo,
 * que la habilita la persona que coordina el cut-over).
 */
export interface FeatureFlagDef {
  key: string;
  label: string;
  description: string;
  default: boolean;
  superadminOnly: boolean;
}

export const FEATURE_FLAGS = [
  {
    key: 'console.legacy_writes',
    label: 'Escritura hacia el stack',
    description:
      'La consola escribe en la DB del stack de n8n (escalaciones atendidas, notas, prioridad, archivo, overrides, re-encolar, prompts). Apagado: la consola es de sólo lectura para esos datos.',
    default: false,
    superadminOnly: true,
  },
  {
    key: 'prompts.require_preview',
    label: 'Probar antes de publicar',
    description:
      'No se puede publicar un prompt sin haber corrido antes la prueba ("Probar") sobre esa versión.',
    default: false,
    superadminOnly: false,
  },
] as const satisfies readonly FeatureFlagDef[];

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[number]['key'];
export const FEATURE_FLAG_KEYS = FEATURE_FLAGS.map((f) => f.key) as [
  FeatureFlagKey,
  ...FeatureFlagKey[],
];
export const featureFlagKeySchema = z.enum(FEATURE_FLAG_KEYS);

/** Valor efectivo: lo guardado en la empresa o, si no está, el default del registro. */
export function flagEnabled(
  flags: Readonly<Record<string, boolean>> | null | undefined,
  key: FeatureFlagKey,
): boolean {
  const stored = flags?.[key];
  if (typeof stored === 'boolean') return stored;
  return FEATURE_FLAGS.find((f) => f.key === key)?.default ?? false;
}

export const tenantFlagsResponseSchema = z.object({
  tenantId: z.uuid(),
  flags: z.array(
    z.object({
      key: featureFlagKeySchema,
      label: z.string(),
      description: z.string(),
      enabled: z.boolean(),
      /** `true` si el valor viene del default (la empresa no lo fijó). */
      isDefault: z.boolean(),
      superadminOnly: z.boolean(),
    }),
  ),
});
export type TenantFlagsResponse = z.infer<typeof tenantFlagsResponseSchema>;

export const updateTenantFlagsSchema = z.object({
  flags: z.partialRecord(featureFlagKeySchema, z.boolean()),
});
export type UpdateTenantFlags = z.infer<typeof updateTenantFlagsSchema>;
